import React from 'react';
import Icon from '../../../components/AppIcon';

const LifecycleOverview = ({ equipment, analytics, workOrders }) => {
  const lifecycleStatusConfig = {
    procurement: { label: 'Procurement', color: 'text-blue-600', bg: 'bg-blue-100', icon: 'ShoppingCart' },
    installation: { label: 'Installation', color: 'text-purple-600', bg: 'bg-purple-100', icon: 'Settings' },
    active: { label: 'Active Use', color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle2' },
    maintenance_required: { label: 'Maintenance Required', color: 'text-warning', bg: 'bg-warning/10', icon: 'AlertTriangle' },
    end_of_life: { label: 'End of Life', color: 'text-error', bg: 'bg-error/10', icon: 'AlertCircle' },
    disposed: { label: 'Disposed', color: 'text-muted-foreground', bg: 'bg-muted', icon: 'Archive' }
  };

  const getUpcomingMaintenance = () => {
    const today = new Date();
    const upcoming = equipment?.filter((eq) => {
      if (!eq?.next_maintenance_date) return false;
      const nextDate = new Date(eq?.next_maintenance_date);
      const daysUntil = Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24));
      return daysUntil >= 0 && daysUntil <= 30;
    });
    return upcoming?.sort((a, b) => new Date(a?.next_maintenance_date) - new Date(b?.next_maintenance_date));
  };

  const getEndOfLifeEquipment = () => {
    return equipment?.filter((eq) => {
      const remainingLife = parseFloat(eq?.remaining_useful_life_years || 0);
      return remainingLife <= 1;
    });
  };

  const upcomingMaintenance = getUpcomingMaintenance();
  const endOfLifeEquipment = getEndOfLifeEquipment();
  const pendingWorkOrders = workOrders?.filter((wo) => wo?.status === 'pending' || wo?.status === 'in_progress');

  return (
    <div className="space-y-6">
      {/* Lifecycle Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(lifecycleStatusConfig)?.map(([status, config]) => {
          const count = analytics?.byLifecycleStatus?.[status] || 0;
          return (
            <div key={status} className="bg-card rounded-xl p-6 border border-border hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg ${config?.bg} flex items-center justify-center`}>
                  <Icon name={config?.icon} size={24} className={config?.color} />
                </div>
                <span className="text-3xl font-bold text-foreground">{count}</span>
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">{config?.label}</h3>
            </div>
          );
        })}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Calendar" size={20} className="text-warning" />
            <h3 className="text-sm font-medium text-muted-foreground">Upcoming Maintenance</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{upcomingMaintenance?.length || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Next 30 days</p>
        </div>

        <div className="bg-card rounded-xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="ClipboardList" size={20} className="text-primary" />
            <h3 className="text-sm font-medium text-muted-foreground">Active Work Orders</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{pendingWorkOrders?.length || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Pending or in progress</p>
        </div>

        <div className="bg-card rounded-xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="AlertCircle" size={20} className="text-error" />
            <h3 className="text-sm font-medium text-muted-foreground">End of Life</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{endOfLifeEquipment?.length || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Replacement needed</p>
        </div>
      </div>

      {/* Upcoming Maintenance List */}
      {upcomingMaintenance?.length > 0 && (
        <div className="bg-card rounded-xl border border-border">
          <div className="p-6 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="Calendar" size={20} />
              Upcoming Maintenance Schedule
            </h3>
          </div>
          <div className="divide-y divide-border">
            {upcomingMaintenance?.slice(0, 5)?.map((eq) => {
              const daysUntil = Math.ceil(
                (new Date(eq?.next_maintenance_date) - new Date()) / (1000 * 60 * 60 * 24)
              );
              return (
                <div key={eq?.id} className="p-4 hover:bg-muted/30 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{eq?.name}</p>
                      <p className="text-sm text-muted-foreground">{eq?.location}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-foreground">
                        {new Date(eq?.next_maintenance_date)?.toLocaleDateString()}
                      </p>
                      <p className={`text-xs ${
                        daysUntil <= 7 ? 'text-error' : daysUntil <= 14 ? 'text-warning' : 'text-muted-foreground'
                      }`}>
                        {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days`}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* End of Life Equipment */}
      {endOfLifeEquipment?.length > 0 && (
        <div className="bg-card rounded-xl border border-error">
          <div className="p-6 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-error" />
              Equipment Requiring Replacement
            </h3>
          </div>
          <div className="divide-y divide-border">
            {endOfLifeEquipment?.map((eq) => (
              <div key={eq?.id} className="p-4 hover:bg-muted/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{eq?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {eq?.manufacturer} {eq?.model} • {eq?.location}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-error">
                      {eq?.remaining_useful_life_years?.toFixed(1)} years remaining
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Purchased {new Date(eq?.purchase_date)?.toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LifecycleOverview;